import { useEffect, useId, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import type { POI } from "@poi-planner/shared";
import type {
  SortMode,
  TagWithCount,
  TagFilterMode,
  TagMatchMode,
  ActiveFilterCounts,
  OwnerOption,
} from "../hooks/usePoiFilters";
import { legPalette } from "../theme/colors";
import { Button } from "./Button";
import { SegmentedControl } from "./SegmentedControl";
import { hasUsableCoordinates } from "../utils/dayMapPois";

export interface CollectionOption {
  id: string;
  name: string;
}

export interface TripOption {
  id: string;
  name: string;
}

export interface LegOption {
  id: string;
  name: string;
  /** Index into `legPalettes`, so a pill can be matched to its calendar bar. */
  color?: number;
  description?: string;
}

type FilterCategory =
  | "tag"
  | "trip"
  | "collection"
  | "leg"
  | "source"
  | "library";
type ActiveFilterCategory = FilterCategory | "distance";

/** Categories that always stay on the pill row; the rest live behind "More". */
const PRIMARY_CATEGORY_IDS: FilterCategory[] = ["tag", "leg"];

export interface PoiFilterBarProps {
  // From usePoiFilters
  search: string;
  sortMode: SortMode;
  sortAscending: boolean;
  tagFilters: Map<string, TagFilterMode>;
  allTags: TagWithCount[];
  onSearchChange: (value: string) => void;
  onSortModeChange: (mode: SortMode) => void;
  onSortAscendingChange: (ascending: boolean) => void;
  onToggleTag: (tag: string) => void;
  tagMatchMode?: TagMatchMode;
  onTagMatchModeChange?: (mode: TagMatchMode) => void;

  // Source filters (multi-select)
  sourceFilters?: Map<string, TagFilterMode>;
  allSourceTexts?: string[];
  onToggleSource?: (source: string) => void;

  // Collection filters (multi-select)
  collectionFilters?: Map<string, TagFilterMode>;
  collections?: CollectionOption[];
  onToggleCollection?: (collectionId: string) => void;

  // Trip filters (multi-select) - used in Library page
  tripFilters?: Map<string, TagFilterMode>;
  trips?: TripOption[];
  onToggleTrip?: (tripId: string) => void;

  // Leg filters (multi-select) - used in trip sidebar
  legFilters?: Map<string, TagFilterMode>;
  legs?: LegOption[];
  onToggleLeg?: (legId: string) => void;

  // Owner/library filters (multi-select) - used in trip sidebar
  ownerFilters?: Map<string, TagFilterMode>;
  owners?: OwnerOption[];
  onToggleOwner?: (ownerId: string) => void;

  // Clear all filters
  onClearAllFilters?: () => void;
  activeFilterCounts?: ActiveFilterCounts;

  // Optional trip-specific (sidebar)
  hideScheduled?: boolean;
  onHideScheduledChange?: (value: boolean) => void;

  // Optional library-specific
  boundsFilterEnabled?: boolean;
  onBoundsFilterChange?: (value: boolean) => void;

  // Proximity target (both contexts)
  targetPoi?: POI | null;
  onClearTarget?: () => void;
  maximumDistanceKm?: number | null;
  onMaximumDistanceChange?: (distanceKm: number | null) => void;

  // POI counts for filter summary
  filteredCount?: number;
  totalCount?: number;
}

export function PoiFilterBar({
  search,
  sortMode,
  sortAscending,
  tagFilters,
  allTags,
  onSearchChange,
  onSortModeChange,
  onSortAscendingChange,
  onToggleTag,
  tagMatchMode,
  onTagMatchModeChange,
  sourceFilters = new Map(),
  allSourceTexts = [],
  onToggleSource,
  collectionFilters = new Map(),
  collections = [],
  onToggleCollection,
  tripFilters = new Map(),
  trips = [],
  onToggleTrip,
  legFilters = new Map(),
  legs = [],
  onToggleLeg,
  ownerFilters = new Map(),
  owners = [],
  onToggleOwner,
  onClearAllFilters,
  activeFilterCounts,
  hideScheduled,
  onHideScheduledChange,
  boundsFilterEnabled,
  onBoundsFilterChange,
  targetPoi,
  onClearTarget,
  maximumDistanceKm = null,
  onMaximumDistanceChange,
  filteredCount,
  totalCount,
}: PoiFilterBarProps) {
  // Active filter category for the swappable cloud
  const [activeCategory, setActiveCategory] = useState<FilterCategory>("tag");

  // Whether the secondary (Collection/Trip/Source/Library) pills are shown.
  const [secondaryExpanded, setSecondaryExpanded] = useState(false);

  // Expanded filter summary state
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  // A controlled search value keeps the panel visible without becoming
  // sticky local state when the parent clears that value.
  const [searchOpened, setSearchOpened] = useState(false);
  const searchExpanded = searchOpened || Boolean(search);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const maximumDistanceDescriptionId = useId();
  const [maximumDistanceDraft, setMaximumDistanceDraft] = useState(
    maximumDistanceKm?.toString() ?? "",
  );
  const skipNextMaximumDistanceSync = useRef(false);
  const previousTargetPoiId = useRef(targetPoi?.id);

  useEffect(() => {
    if (skipNextMaximumDistanceSync.current) {
      skipNextMaximumDistanceSync.current = false;
      return;
    }
    setMaximumDistanceDraft(maximumDistanceKm?.toString() ?? "");
  }, [maximumDistanceKm]);

  useEffect(() => {
    if (targetPoi?.id !== previousTargetPoiId.current) {
      previousTargetPoiId.current = targetPoi?.id;
      skipNextMaximumDistanceSync.current = false;
      setMaximumDistanceDraft("");
    }
  }, [targetPoi?.id]);

  const parsedMaximumDistance = Number(maximumDistanceDraft);
  const maximumDistanceInvalid =
    maximumDistanceDraft !== "" &&
    (!Number.isFinite(parsedMaximumDistance) || parsedMaximumDistance <= 0);
  const hasProximityTarget = Boolean(
    targetPoi && hasUsableCoordinates(targetPoi),
  );

  const handleMaximumDistanceChange = (value: string) => {
    setMaximumDistanceDraft(value);
    if (value === "") {
      onMaximumDistanceChange?.(null);
      return;
    }

    const distanceKm = Number(value);
    if (Number.isFinite(distanceKm) && distanceKm > 0) {
      onMaximumDistanceChange?.(distanceKm);
      return;
    }

    skipNextMaximumDistanceSync.current = true;
    onMaximumDistanceChange?.(null);
  };

  useEffect(() => {
    if (searchExpanded) searchInputRef.current?.focus();
  }, [searchExpanded]);

  const closeSearch = () => {
    onSearchChange("");
    setSearchOpened(false);
  };

  // Cloud search state (for filtering within active category)
  const [cloudSearch, setCloudSearch] = useState("");
  const cloudSearchRef = useRef<HTMLInputElement>(null);

  // Clear cloud search when switching categories
  useEffect(() => {
    setCloudSearch("");
  }, [activeCategory]);

  // Determine if we should show the category selector (Library page has all 4)
  const hasMultipleCategories =
    onToggleTrip ||
    onToggleCollection ||
    onToggleLeg ||
    onToggleSource ||
    onToggleOwner;

  // Calculate filter counts if not provided
  const filterCounts = activeFilterCounts ?? {
    tag: tagFilters.size,
    trip: tripFilters.size,
    collection: collectionFilters.size,
    leg: legFilters.size,
    source: sourceFilters.size,
    owner: ownerFilters.size,
    distance: maximumDistanceKm === null ? 0 : 1,
    total:
      tagFilters.size +
      tripFilters.size +
      collectionFilters.size +
      legFilters.size +
      sourceFilters.size +
      ownerFilters.size +
      (maximumDistanceKm === null ? 0 : 1),
  };

  // Filter tags/items by cloud search
  const filteredTags = useMemo(() => {
    if (!cloudSearch.trim()) return allTags;
    const q = cloudSearch.toLowerCase();
    return allTags.filter((t) => t.label.toLowerCase().includes(q));
  }, [allTags, cloudSearch]);

  const filteredTrips = useMemo(() => {
    if (!cloudSearch.trim()) return trips;
    const q = cloudSearch.toLowerCase();
    return trips.filter((t) => t.name.toLowerCase().includes(q));
  }, [trips, cloudSearch]);

  const filteredCollections = useMemo(() => {
    if (!cloudSearch.trim()) return collections;
    const q = cloudSearch.toLowerCase();
    return collections.filter((c) => c.name.toLowerCase().includes(q));
  }, [collections, cloudSearch]);

  const filteredLegs = useMemo(() => {
    if (!cloudSearch.trim()) return legs;
    const q = cloudSearch.toLowerCase();
    return legs.filter((l) => l.name.toLowerCase().includes(q));
  }, [legs, cloudSearch]);

  const filteredSources = useMemo(() => {
    if (!cloudSearch.trim()) return allSourceTexts;
    const q = cloudSearch.toLowerCase();
    return allSourceTexts.filter((s) => s.toLowerCase().includes(q));
  }, [allSourceTexts, cloudSearch]);

  const filteredOwners = useMemo(() => {
    if (!cloudSearch.trim()) return owners;
    const q = cloudSearch.toLowerCase();
    return owners.filter((o) => o.name.toLowerCase().includes(q));
  }, [owners, cloudSearch]);

  // Base pill classes
  const basePillClasses =
    "inline-flex items-center px-2.5 py-1 text-xs font-medium cursor-pointer transition-all duration-150";

  const selectedPillShadow =
    "shadow-[0_0_0_1px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.2)]";

  // Pill style helper
  const getPillClasses = (
    mode: TagFilterMode | undefined,
    baseColor: string,
    unselectedClasses: string,
  ) => {
    if (mode === "exclude") {
      return "bg-error text-white ring-1 ring-error/50 shadow-sm";
    }
    if (mode === "include") {
      return `${baseColor} text-white ${selectedPillShadow}`;
    }
    return unselectedClasses;
  };

  const unselectedPillClasses =
    "bg-white text-slate-500 shadow-[0_0_0_1px_rgba(0,0,0,0.06)] hover:shadow-[0_0_0_1px_rgba(0,0,0,0.1)] hover:bg-slate-50";

  // Build active filters list for summary
  const activeFilters = useMemo(() => {
    const filters: {
      key: string;
      label: string;
      category: ActiveFilterCategory;
      mode: TagFilterMode;
      /** Leg palette index, so a summary chip matches its calendar bar. */
      legColor?: number;
    }[] = [];

    tagFilters.forEach((mode, tag) => {
      const option = allTags.find((item) => item.tag === tag);
      filters.push({
        key: `tag-${tag}`,
        label: option?.label ?? tag,
        category: "tag",
        mode,
      });
    });

    tripFilters.forEach((mode, tripId) => {
      const trip = trips.find((t) => t.id === tripId);
      if (trip) {
        filters.push({
          key: `trip-${tripId}`,
          label: trip.name,
          category: "trip",
          mode,
        });
      }
    });

    collectionFilters.forEach((mode, collectionId) => {
      const collection = collections.find((c) => c.id === collectionId);
      if (collection) {
        filters.push({
          key: `collection-${collectionId}`,
          label: collection.name,
          category: "collection",
          mode,
        });
      }
    });

    legFilters.forEach((mode, legId) => {
      const leg = legs.find((l) => l.id === legId);
      if (leg) {
        filters.push({
          key: `leg-${legId}`,
          label: leg.name,
          category: "leg",
          mode,
          legColor: leg.color,
        });
      }
    });

    sourceFilters.forEach((mode, source) => {
      filters.push({
        key: `source-${source}`,
        label: source,
        category: "source",
        mode,
      });
    });

    ownerFilters.forEach((mode, ownerId) => {
      const owner = owners.find((o) => o.id === ownerId);
      if (owner) {
        filters.push({
          key: `library-${ownerId}`,
          label: owner.name,
          category: "library",
          mode,
        });
      }
    });

    if (maximumDistanceKm !== null) {
      filters.push({
        key: "maximum-distance",
        label: `Within ${maximumDistanceKm} km of ${targetPoi?.name ?? "target"}`,
        category: "distance",
        mode: "include",
      });
    }

    return filters;
  }, [
    tagFilters,
    allTags,
    tripFilters,
    collectionFilters,
    legFilters,
    sourceFilters,
    ownerFilters,
    trips,
    collections,
    legs,
    owners,
    maximumDistanceKm,
    targetPoi?.name,
  ]);

  // Handle removing a filter from summary
  const handleRemoveFilter = (filter: {
    category: ActiveFilterCategory;
    label: string;
    key: string;
  }) => {
    switch (filter.category) {
      case "tag":
        onToggleTag(filter.label);
        break;
      case "trip":
        if (onToggleTrip) {
          const tripId = filter.key.replace("trip-", "");
          onToggleTrip(tripId);
        }
        break;
      case "collection":
        if (onToggleCollection) {
          const collectionId = filter.key.replace("collection-", "");
          onToggleCollection(collectionId);
        }
        break;
      case "leg":
        if (onToggleLeg) {
          onToggleLeg(filter.key.replace("leg-", ""));
        }
        break;
      case "source":
        if (onToggleSource) {
          onToggleSource(filter.label);
        }
        break;
      case "library":
        if (onToggleOwner) {
          const ownerId = filter.key.replace("library-", "");
          onToggleOwner(ownerId);
        }
        break;
      case "distance":
        onMaximumDistanceChange?.(null);
        break;
    }
  };

  // Category definitions
  const allCategories: {
    id: FilterCategory;
    label: string;
    count: number;
    available: boolean;
  }[] = [
    { id: "tag", label: "Tag", count: filterCounts.tag, available: true },
    {
      id: "leg",
      label: "Leg",
      count: filterCounts.leg,
      available: !!onToggleLeg && legs.length > 0,
    },
    {
      id: "collection",
      label: "Collection",
      count: filterCounts.collection,
      available: !!onToggleCollection && collections.length > 0,
    },
    {
      id: "trip",
      label: "Trip",
      count: filterCounts.trip,
      available: !!onToggleTrip && trips.length > 0,
    },
    {
      id: "source",
      label: "Source",
      count: filterCounts.source,
      available: !!onToggleSource && allSourceTexts.length > 0,
    },
    {
      id: "library",
      label: "Library",
      count: filterCounts.owner,
      // Show whenever at least one foreign owner is present, even if the
      // caller has zero POIs of their own (so there's only one owner group
      // total) — that's exactly when the toggle is needed most.
      available: !!onToggleOwner && owners.some((o) => !o.isSelf),
    },
  ];
  const categories = allCategories.filter((c) => c.available);

  // Tag and Leg are the everyday filters, so they stay on the row. The rest
  // stay behind the expander unless they are in use — a category that is
  // selected or already filtering must never hide the control that undoes it.
  const primaryCategories = categories.filter((c) =>
    PRIMARY_CATEGORY_IDS.includes(c.id),
  );
  const secondaryCategories = categories.filter(
    (c) => !PRIMARY_CATEGORY_IDS.includes(c.id),
  );
  const visibleSecondaryCategories = secondaryExpanded
    ? secondaryCategories
    : secondaryCategories.filter((c) => c.id === activeCategory || c.count > 0);
  const hiddenSecondaryCount =
    secondaryCategories.length - visibleSecondaryCategories.length;

  return (
    <>
      {/* Filter Summary - shown when filters are active and counts are provided */}
      {filteredCount !== undefined && totalCount !== undefined && (
        <div className="mb-2">
          <div
            className={clsx(
              "flex items-center justify-between px-2.5 py-1.5 rounded-lg transition-colors",
              filterCounts.total > 0
                ? "bg-slate-100 cursor-pointer hover:bg-slate-150"
                : "bg-slate-50",
            )}
            onClick={() =>
              filterCounts.total > 0 && setSummaryExpanded(!summaryExpanded)
            }
          >
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-primary">
                {filteredCount === totalCount
                  ? `${totalCount} ${totalCount === 1 ? "POI" : "POIs"}`
                  : `${filteredCount} of ${totalCount} POIs`}
              </span>
              {filterCounts.total > 0 && (
                <>
                  <span className="text-tertiary">•</span>
                  <span className="text-secondary">
                    {filterCounts.total}{" "}
                    {filterCounts.total === 1 ? "filter" : "filters"}
                  </span>
                  <span className="material-symbols-outlined text-sm text-tertiary">
                    {summaryExpanded ? "expand_less" : "expand_more"}
                  </span>
                </>
              )}
            </div>
            {filterCounts.total > 0 && onClearAllFilters && (
              <button
                aria-label="Clear all filters"
                onClick={(e) => {
                  e.stopPropagation();
                  setMaximumDistanceDraft("");
                  onClearAllFilters();
                  setSummaryExpanded(false);
                }}
                className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-error hover:text-error hover:bg-error-bg rounded transition-colors"
              >
                Clear
                <span className="material-symbols-outlined text-xs">close</span>
              </button>
            )}
          </div>

          {/* Expanded filter details */}
          {summaryExpanded && filterCounts.total > 0 && (
            <div className="mt-1.5 px-2.5 py-2 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex flex-wrap gap-1.5">
                {activeFilters.map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => handleRemoveFilter(filter)}
                    title="Click to remove"
                    // Included leg chips are painted from the leg's own
                    // palette entry so a chip can be matched to its bar.
                    style={
                      filter.mode !== "exclude" && filter.legColor !== undefined
                        ? {
                            backgroundColor: legPalette(filter.legColor).bg,
                            color: legPalette(filter.legColor).text,
                          }
                        : undefined
                    }
                    className={clsx(
                      "inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full transition-all",
                      filter.mode === "exclude"
                        ? "bg-error-bg-strong text-error hover:bg-error-bg-strong"
                        : filter.category === "tag"
                          ? "bg-slate-200 text-slate-700 hover:bg-slate-300"
                          : filter.category === "trip"
                            ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                            : filter.category === "collection"
                              ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                              : filter.category === "leg"
                                ? "hover:brightness-95"
                                : filter.category === "library"
                                  ? "bg-sky-100 text-sky-700 hover:bg-sky-200"
                                  : filter.category === "distance"
                                    ? "bg-primary/10 text-primary hover:bg-primary/15"
                                    : "bg-slate-200 text-slate-600 hover:bg-slate-300",
                    )}
                  >
                    {filter.mode === "exclude" && (
                      <span className="material-symbols-outlined text-xs">
                        remove
                      </span>
                    )}
                    {filter.label}
                    <span className="material-symbols-outlined text-xs">
                      close
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Category Selector - only show if we have multiple filter types */}
      {hasMultipleCategories && categories.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          {[...primaryCategories, ...visibleSecondaryCategories].map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={clsx(
                "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full transition-all",
                "border",
                activeCategory === cat.id
                  ? "border-transparent bg-slate-700 text-white shadow-sm"
                  : "bg-transparent border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-600",
              )}
            >
              {cat.label}
              {cat.count > 0 && (
                <span
                  className={clsx(
                    "inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-xs font-bold rounded-full",
                    activeCategory === cat.id
                      ? "bg-white/20 text-white"
                      : "bg-blue-500 text-white",
                  )}
                >
                  {cat.count}
                </span>
              )}
            </button>
          ))}
          {secondaryCategories.length > 0 &&
            (secondaryExpanded || hiddenSecondaryCount > 0) && (
              <Button
                variant="unstyled"
                onClick={() => setSecondaryExpanded(!secondaryExpanded)}
                aria-expanded={secondaryExpanded}
                aria-label={
                  secondaryExpanded
                    ? "Show fewer filter categories"
                    : "Show more filter categories"
                }
                className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-transparent border border-slate-300 text-slate-500 transition-all hover:border-slate-400 hover:text-slate-600"
              >
                {/* Inline chevron rather than a Material Symbol: the icon font
                    ships its own unlayered 24px font-size, which would make
                    this pill taller than the category pills beside it. */}
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path
                    d={secondaryExpanded ? "M15 6 9 12l6 6" : "M9 6l6 6-6 6"}
                  />
                </svg>
              </Button>
            )}
        </div>
      )}

      {/* Cloud search - show if category has many items */}
      {((activeCategory === "tag" && allTags.length > 8) ||
        (activeCategory === "trip" && trips.length > 8) ||
        (activeCategory === "collection" && collections.length > 8) ||
        (activeCategory === "leg" && legs.length > 8) ||
        (activeCategory === "source" && allSourceTexts.length > 8) ||
        (activeCategory === "library" && owners.length > 8)) && (
        <div className="relative mb-1.5">
          <span className="material-symbols-outlined absolute left-2 top-1.5 text-tertiary text-sm">
            search
          </span>
          <input
            ref={cloudSearchRef}
            type="text"
            placeholder={`Search ${activeCategory}s...`}
            value={cloudSearch}
            onChange={(e) => setCloudSearch(e.target.value)}
            className="w-full pl-7 pr-7 py-1 bg-slate-50 border-none rounded text-xs outline-none text-primary font-sans"
          />
          {cloudSearch && (
            <button
              onClick={() => setCloudSearch("")}
              className="absolute right-1 top-1 p-0.5 text-tertiary hover:text-primary transition-colors"
            >
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
          )}
        </div>
      )}

      {activeCategory === "tag" &&
        tagMatchMode !== undefined &&
        onTagMatchModeChange && (
          <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-border bg-white px-2.5 py-2">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-primary">Match tags</p>
              <p className="truncate text-2xs text-tertiary">
                {tagMatchMode === "all"
                  ? "Every selected tag"
                  : "At least one selected tag"}
              </p>
            </div>
            <SegmentedControl
              aria-label="Tag matching mode"
              value={tagMatchMode}
              onChange={onTagMatchModeChange}
              options={[
                { value: "all", label: "All", ariaLabel: "Match all tags" },
                { value: "any", label: "Any", ariaLabel: "Match any tag" },
              ]}
            />
          </div>
        )}

      {/* Swappable Cloud Content */}
      <div className="flex items-start gap-1.5 flex-wrap max-h-[120px] overflow-y-auto custom-scrollbar">
        {/* Tag Cloud */}
        {activeCategory === "tag" &&
          filteredTags.map(({ tag, label, count, kind, ownerName }) => {
            const filterMode = tagFilters.get(tag);
            const isCategory = kind === "category";
            return (
              <button
                key={tag}
                onClick={() => onToggleTag(tag)}
                aria-pressed={filterMode === "include"}
                aria-label={`${label}${ownerName ? ` ${ownerName}` : ""}${filterMode === "exclude" ? "" : ` ${count}`}`}
                title={`${isCategory ? "Category" : "Tag"}: ${
                  !filterMode
                    ? "click to include"
                    : filterMode === "include"
                      ? "click to exclude"
                      : "click to clear"
                }`}
                className={clsx(
                  basePillClasses,
                  "gap-1 rounded-full",
                  getPillClasses(
                    filterMode,
                    "bg-slate-800",
                    unselectedPillClasses,
                  ),
                )}
              >
                {isCategory && (
                  <span
                    className="inline-flex size-3 shrink-0 items-center justify-center text-xs font-bold leading-none"
                    aria-hidden="true"
                  >
                    +
                  </span>
                )}
                <span>{label}</span>
                {ownerName && (
                  <span className="max-w-16 truncate text-2xs opacity-70">
                    {ownerName}
                  </span>
                )}
                {filterMode !== "exclude" && (
                  <span
                    className={clsx(
                      "tabular-nums text-xs font-semibold",
                      filterMode ? "text-white/60" : "text-slate-400",
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}

        {/* Trip Cloud */}
        {activeCategory === "trip" &&
          onToggleTrip &&
          filteredTrips.map((trip) => {
            const filterMode = tripFilters.get(trip.id);
            return (
              <button
                key={trip.id}
                onClick={() => onToggleTrip(trip.id)}
                title={
                  !filterMode
                    ? "Click to include"
                    : filterMode === "include"
                      ? "Click to exclude"
                      : "Click to clear"
                }
                className={clsx(
                  basePillClasses,
                  "rounded-full",
                  getPillClasses(
                    filterMode,
                    "bg-purple-600",
                    unselectedPillClasses,
                  ),
                )}
              >
                {trip.name}
              </button>
            );
          })}

        {/* Collection Cloud */}
        {activeCategory === "collection" &&
          onToggleCollection &&
          filteredCollections.map((collection) => {
            const filterMode = collectionFilters.get(collection.id);
            return (
              <button
                key={collection.id}
                onClick={() => onToggleCollection(collection.id)}
                title={
                  !filterMode
                    ? "Click to include"
                    : filterMode === "include"
                      ? "Click to exclude"
                      : "Click to clear"
                }
                className={clsx(
                  basePillClasses,
                  "rounded-full",
                  getPillClasses(
                    filterMode,
                    "bg-amber-600",
                    unselectedPillClasses,
                  ),
                )}
              >
                {collection.name}
              </button>
            );
          })}

        {/* Leg Cloud */}
        {activeCategory === "leg" &&
          onToggleLeg &&
          filteredLegs.map((leg) => {
            const filterMode = legFilters.get(leg.id);
            return (
              <Button
                key={leg.id}
                variant="unstyled"
                onClick={() => onToggleLeg(leg.id)}
                title={`${leg.description ? `${leg.description}. ` : ""}${
                  !filterMode
                    ? "Click to include"
                    : filterMode === "include"
                      ? "Click to exclude"
                      : "Click to clear"
                }`}
                aria-label={`${leg.name}${leg.description ? ` — ${leg.description}` : ""}`}
                className={clsx(
                  basePillClasses,
                  "gap-1.5 rounded-full",
                  filterMode === "exclude"
                    ? "bg-error text-white ring-1 ring-error/50 shadow-sm"
                    : filterMode === "include"
                      ? clsx(
                          "text-white",
                          selectedPillShadow,
                          leg.color === undefined && "bg-neutral-600",
                        )
                      : unselectedPillClasses,
                )}
                // Runtime palette values: each leg carries its own colour
                // index, so its pill matches its bar in the all-day band.
                style={
                  filterMode === "include" && leg.color !== undefined
                    ? { backgroundColor: legPalette(leg.color).dot }
                    : undefined
                }
              >
                {filterMode !== "exclude" && (
                  <span
                    aria-hidden
                    className="size-2 shrink-0 rounded-full"
                    style={{
                      backgroundColor:
                        filterMode === "include"
                          ? "currentColor"
                          : leg.color === undefined
                            ? "var(--color-neutral-400)"
                            : legPalette(leg.color).dot,
                    }}
                  />
                )}
                {leg.name}
              </Button>
            );
          })}

        {/* Source Cloud */}
        {activeCategory === "source" &&
          onToggleSource &&
          filteredSources.map((source) => {
            const filterMode = sourceFilters.get(source);
            return (
              <button
                key={source}
                onClick={() => onToggleSource(source)}
                title={
                  !filterMode
                    ? "Click to include"
                    : filterMode === "include"
                      ? "Click to exclude"
                      : "Click to clear"
                }
                className={clsx(
                  basePillClasses,
                  "rounded-full",
                  getPillClasses(
                    filterMode,
                    "bg-slate-700",
                    unselectedPillClasses,
                  ),
                )}
              >
                {source}
              </button>
            );
          })}

        {/* Library Cloud */}
        {activeCategory === "library" &&
          onToggleOwner &&
          filteredOwners.map((owner) => {
            const filterMode = ownerFilters.get(owner.id);
            return (
              <button
                key={owner.id}
                onClick={() => onToggleOwner(owner.id)}
                title={
                  !filterMode
                    ? "Click to include"
                    : filterMode === "include"
                      ? "Click to exclude"
                      : "Click to clear"
                }
                className={clsx(
                  basePillClasses,
                  "rounded-full",
                  getPillClasses(
                    filterMode,
                    "bg-sky-600",
                    unselectedPillClasses,
                  ),
                )}
              >
                {owner.name}
              </button>
            );
          })}

        {/* Empty states */}
        {activeCategory === "tag" && filteredTags.length === 0 && (
          <span className="text-xs text-tertiary italic">
            {cloudSearch ? "No matching tags" : "No tags available"}
          </span>
        )}
        {activeCategory === "trip" && filteredTrips.length === 0 && (
          <span className="text-xs text-tertiary italic">
            {cloudSearch ? "No matching trips" : "No trips available"}
          </span>
        )}
        {activeCategory === "collection" &&
          filteredCollections.length === 0 && (
            <span className="text-xs text-tertiary italic">
              {cloudSearch
                ? "No matching collections"
                : "No collections available"}
            </span>
          )}
        {activeCategory === "leg" && filteredLegs.length === 0 && (
          <span className="text-xs text-tertiary italic">
            {cloudSearch ? "No matching legs" : "No legs available"}
          </span>
        )}
        {activeCategory === "source" && filteredSources.length === 0 && (
          <span className="text-xs text-tertiary italic">
            {cloudSearch ? "No matching sources" : "No sources available"}
          </span>
        )}
        {activeCategory === "library" && filteredOwners.length === 0 && (
          <span className="text-xs text-tertiary italic">
            {cloudSearch ? "No matching libraries" : "No libraries available"}
          </span>
        )}
      </div>

      {/* Sort + Icon toggle buttons - compact row */}
      <div className="mt-2 flex flex-nowrap items-center gap-1.5">
        {/* Target badge (replaces sort dropdown when active) */}
        {sortMode === "distance" && targetPoi ? (
          <div className="flex min-w-0 flex-1 items-center gap-1 rounded-md border border-blue-500/30 bg-blue-500/10 px-1.5 py-1 text-xs font-semibold text-blue-500">
            <span className="material-symbols-outlined shrink-0 text-sm">
              gps_fixed
            </span>
            <span className="min-w-0 flex-1 truncate">{targetPoi.name}</span>
            <button
              type="button"
              onClick={() => onClearTarget?.()}
              aria-label="Clear proximity target"
              className="flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-blue-500/20 p-0 text-blue-500 transition-colors hover:bg-blue-500/30"
            >
              <span className="material-symbols-outlined text-xs">close</span>
            </button>
          </div>
        ) : (
          <select
            value={sortMode}
            onChange={(e) => onSortModeChange(e.target.value as SortMode)}
            aria-label="Sort POIs"
            className="w-24 shrink-0 cursor-pointer rounded-md border-none bg-subtle px-2 py-1 font-sans text-xs font-semibold text-secondary outline-none"
          >
            <option value="alpha">A → Z</option>
            <option value="timeBudget">Time</option>
            <option value="recentlyAdded">Recent</option>
          </select>
        )}

        {hasProximityTarget && onMaximumDistanceChange && (
          <div className="relative w-16 shrink-0">
            <input
              id={`${maximumDistanceDescriptionId}-input`}
              type="number"
              inputMode="decimal"
              min="0.1"
              step="0.1"
              placeholder="Any"
              value={maximumDistanceDraft}
              onChange={(event) =>
                handleMaximumDistanceChange(event.target.value)
              }
              aria-label="Maximum distance from target"
              aria-invalid={maximumDistanceInvalid}
              aria-describedby={maximumDistanceDescriptionId}
              className={clsx(
                "h-7 w-full rounded-md border bg-white pl-2 pr-6 text-xs tabular-nums text-primary outline-none transition [appearance:textfield] focus-visible:ring-2 focus-visible:ring-accent/20 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
                maximumDistanceInvalid
                  ? "border-error focus-visible:border-error"
                  : "border-border focus-visible:border-accent",
              )}
            />
            <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-2xs font-semibold text-tertiary">
              km
            </span>
          </div>
        )}

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {/* Sort direction */}
          <button
            type="button"
            onClick={() => onSortAscendingChange(!sortAscending)}
            aria-label={sortAscending ? "Sort descending" : "Sort ascending"}
            title={sortAscending ? "Sort descending" : "Sort ascending"}
            className="flex size-7 items-center justify-center rounded-md border-none bg-subtle p-0 text-secondary transition-colors hover:bg-slate-200"
          >
            <span className="material-symbols-outlined text-base">
              {sortAscending ? "arrow_upward" : "arrow_downward"}
            </span>
          </button>

          {/* Hide scheduled toggle - icon only */}
          {onHideScheduledChange && (
            <button
              type="button"
              onClick={() => onHideScheduledChange(!hideScheduled)}
              aria-label={
                hideScheduled ? "Show scheduled POIs" : "Hide scheduled POIs"
              }
              title={hideScheduled ? "Show all POIs" : "Hide scheduled POIs"}
              className={clsx(
                "flex size-7 items-center justify-center rounded-md p-0 transition-all",
                hideScheduled
                  ? "border border-primary bg-primary text-white"
                  : "border border-border bg-white text-secondary hover:bg-slate-50",
              )}
            >
              <span className="material-symbols-outlined text-base">
                {hideScheduled ? "visibility_off" : "visibility"}
              </span>
            </button>
          )}

          {/* Bounds filter toggle - icon only */}
          {onBoundsFilterChange && (
            <button
              type="button"
              onClick={() => onBoundsFilterChange(!boundsFilterEnabled)}
              aria-label={
                boundsFilterEnabled ? "Show all map POIs" : "Filter to map view"
              }
              title={
                boundsFilterEnabled ? "Show all POIs" : "Filter to map view"
              }
              className={clsx(
                "flex size-7 items-center justify-center rounded-md p-0 transition-all",
                boundsFilterEnabled
                  ? "border border-blue-600 bg-blue-600 text-white"
                  : "border border-border bg-white text-secondary hover:bg-slate-50",
              )}
            >
              <span className="material-symbols-outlined text-base">
                {boundsFilterEnabled ? "crop_free" : "frame_inspect"}
              </span>
            </button>
          )}

          <button
            type="button"
            onClick={() =>
              searchExpanded ? closeSearch() : setSearchOpened(true)
            }
            aria-label={searchExpanded ? "Close POI search" : "Search POIs"}
            aria-expanded={searchExpanded}
            aria-controls="poi-search-panel"
            title={searchExpanded ? "Close search" : "Search POIs"}
            className={clsx(
              "flex size-7 shrink-0 items-center justify-center rounded-md p-0 transition-all",
              searchExpanded
                ? "border border-primary bg-primary text-white"
                : "border border-border bg-white text-secondary hover:bg-slate-50",
            )}
          >
            <span className="material-symbols-outlined text-base">search</span>
          </button>
        </div>
      </div>

      {hasProximityTarget && onMaximumDistanceChange && (
        <p
          id={maximumDistanceDescriptionId}
          role={maximumDistanceInvalid ? "alert" : undefined}
          className={clsx(
            maximumDistanceInvalid
              ? "mt-1 text-2xs leading-tight text-error"
              : "sr-only",
          )}
        >
          {maximumDistanceInvalid
            ? "Enter a distance greater than 0 km"
            : `From ${targetPoi?.name ?? "target"}; leave blank for any distance`}
        </p>
      )}

      {searchExpanded && (
        <div id="poi-search-panel" className="relative mt-2">
          <span className="material-symbols-outlined pointer-events-none absolute left-2.5 top-1.5 text-lg text-secondary">
            search
          </span>
          <input
            ref={searchInputRef}
            type="search"
            aria-label="Search POIs"
            placeholder="Search POIs..."
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                closeSearch();
              }
            }}
            className="w-full rounded-lg border-none bg-subtle py-1.5 pl-9 pr-9 font-sans text-sm text-primary outline-none"
          />
          <button
            type="button"
            onClick={closeSearch}
            aria-label="Clear and close search"
            className="absolute right-1 top-0.5 flex size-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-0 text-secondary transition-colors hover:text-primary"
          >
            <span className="material-symbols-outlined text-base">close</span>
          </button>
        </div>
      )}
    </>
  );
}
